import { supabase } from '../supabaseClient';

/* Segédletek a tananyagban (Plusz-segédletek 1. kör).
   A modul ↔ segédlet kapcsolás adat (module_resources tábla a landing
   migrációjában); a module_resources_for_course RPC adja a kurzus összes
   modulja kész segédleteit, és hogy a hívó letöltheti-e (ugyanaz a szabály,
   mint a letöltésnél: a profil szintje; a próba nem nyit). Egy hívás kurzusonként. */
export async function loadModuleResources(courseId) {
  if (!courseId) return {};
  const { data, error } = await supabase.rpc('module_resources_for_course', { p_course: courseId });
  if (error) { console.warn('module_resources_for_course:', error.message); return {}; }
  const byModule = {};
  (data || []).forEach(r => { (byModule[r.module_id] ||= []).push(r); });
  return byModule;
}

/* Letöltés a landing get-resource-url függvényén át: a szintet a szerver
   ellenőrzi, a link rövid életű (a fizetős PDF személyre szabott példány).
   Ingyenes segédlet bejelentkezve azonnal jár, e-mail-kérés nélkül.
   Eredmény: { url } | { korlat } (a szerveroldali letöltési korlát, 429) | { appban: true } (olvasó 3: a PDF
   csak PRO-nak jár, 403 CSAK_APPBAN) | null (hiba). */
export async function resourceUrl(slug, fajl) {
  const { data, error } = await supabase.functions.invoke('get-resource-url', { body: { slug, ...(fajl ? { fajl } : {}) } });
  if (error?.context?.status === 403) {
    const code = await error.context.json().then(j => j?.error || null, () => null);
    if (code === 'CSAK_APPBAN') return { appban: true };
  }
  if (error?.context?.status === 429) {
    const korlat = await error.context.json().then(j => j?.korlat || null, () => null);
    return { korlat };
  }
  if (error || !data?.url) return null;
  return { url: data.url };
}

/* A korlát-üzenet (ugyanaz, mint a landing könyvtárában). */
export function limitMessage(korlat) {
  if (korlat === 'pdf_anyag_30nap') return 'Ezt a PDF-et az elmúlt 30 napban már kétszer letöltötted. Az appban bármikor olvashatod. Ha valami nem működik, írj nekünk: hello@web3kurzus.hu';
  if (korlat === 'pdf_nap') return 'Ma már öt PDF-et letöltöttél. Holnap újra elérhető, az appban addig is olvashatod. Ha valami nem működik, írj nekünk: hello@web3kurzus.hu';
  return korlat === 'havi_osszes'
    ? 'Az elmúlt hónapban már sok anyagot letöltöttél. Pár nap múlva újra elérhető. Ha valami nem működik, írj nekünk: hello@web3kurzus.hu'
    : 'Ma már sok anyagot letöltöttél. Holnap újra elérhető. Ha valami nem működik, írj nekünk: hello@web3kurzus.hu';
}

const VARIANT = { sotet: 'Sötét PDF', nyomtathato: 'Nyomtatható PDF' };
/** „Sötét PDF”, „Nyomtatható PDF”, „Excel” */
export function fileLabel(f) {
  const ext = (f?.fajl || '').split('.').pop().toLowerCase();
  if (ext === 'xlsx') return 'Excel';
  if (ext === 'png') return 'Háttérkép (PNG)';
  return VARIANT[f?.valtozat] || 'PDF';
}

/* ── Olvasó 3. kör: PDF-letöltés csak PRO-nak (és az átmenetben lévő Plusz-előfizetőknek) ── */

/** A PDF-anyag (a `szabad` kivételével) az appban olvasható; a PDF-letöltés csak PRO-nak jár. */
export const isReaderOnly = (r) => r.szint !== 'szabad' && /\.pdf$/i.test(r.fajlok?.[0]?.fajl || '');

/** PDF-jog és keret (segedlet_pdf_allapot): { jog, szint, napi_maradek, anyagok: { slug: maradék } }.
    Oldalbetöltésenként egyszer (a modulkártyák mind ezt használják); letöltés után a refresh frissít. */
let pdfPromise = null;
export function pdfAllapot({ refresh = false } = {}) {
  if (!pdfPromise || refresh) {
    pdfPromise = supabase.rpc('segedlet_pdf_allapot').then(({ data, error }) => (error ? null : data), () => null)
      .then(d => { if (!d) pdfPromise = null; return d; });
  }
  return pdfPromise;
}

/** A modulkártya letöltési része: 'pdf' (PRO, átmenet) | 'pro_sor' (Plusz) | 'fajlok' (Excel, szabad) | 'unlock' | null. */
export function cardActions(r, pdf) {
  if (isReaderOnly(r)) {
    if (!r.can_download) return r.szint === 'ingyenes' ? null : 'unlock';
    if (pdf?.jog) return 'pdf';
    return pdf?.szint === 'plusz' ? 'pro_sor' : null;
  }
  return r.can_download ? 'fajlok' : 'unlock';
}

/** A PDF-gomb felirata a hátralévő kerettel. */
export function pdfButtonLabel(maradek, napi) {
  if (maradek === 0) return 'PDF · a 30 napos keret elfogyott';
  if (napi === 0) return 'PDF · ma már nem, holnap újra';
  return maradek == null ? 'PDF' : `PDF · még ${maradek} letöltés 30 napon belül`;
}

/** „15 oldal · az appban olvasható” / „Excel-sablon” / „PDF” (szabad), mint a landing könyvtárában */
export function formatLine(r) {
  const excel = (r.formatum || '').toLowerCase().startsWith('excel') || (r.fajlok?.[0]?.fajl || '').endsWith('.xlsx');
  if (excel) return 'Excel-sablon';
  const pages = r.oldalak ? `${String(r.oldalak).replace('~', 'kb. ')} oldal · ` : '';
  return `${pages}${isReaderOnly(r) ? 'az appban olvasható' : 'PDF'}`;
}

export const LEVEL_LABEL = { szabad: 'Szabad', ingyenes: 'Ingyenes', tag: 'TAG', plusz: 'Plusz', pro: 'PRO' };
